/* CHI OPERA SU QUESTO WALLET.
 *
 * Aprire un wallet e non vedere quali bot ci stanno dentro è il modo più
 * diretto di prendere una decisione sbagliata: il saldo qui sopra non è fermo,
 * è il saldo di un conto su cui qualcosa sta comprando e vendendo. E in fondo a
 * questa pagina c'è un pulsante che elimina il wallet.
 *
 * Le due cose che questo elenco non nasconde mai:
 *
 *  1. DENARO VERO O SIMULATO, per ogni bot. È un wallet: ci sono dentro soldi
 *     veri, e un bot in prova accanto a uno vero non si distingue dal contesto.
 *  2. IN FUNZIONE O FERMO. Un bot fermo con una posizione aperta ha comunque il
 *     capitale a mercato; un wallet senza nessun bot è un'altra cosa ancora, e
 *     viene detta a parole invece di lasciare il riquadro vuoto.
 *
 * Il vocabolario (le parole «REALE», «SIMULATO», «in funzione», «fermo» e i
 * colori con cui si scrivono) arriva da `componenti/BotInFunzione.tsx`: è lo
 * stesso che usano la lista dei backtest e la schermata Tradebot. Scriverlo qui
 * a mano vorrebbe dire che, il giorno in cui una di quelle cambia, questa resta
 * indietro senza che nessuno se ne accorga.
 *
 * Niente segreti: da un tradebot escono id, strategia, coppia, candela e i due
 * stati. `telegramToken`, `telegramPassword` e `rpc` non vengono nemmeno
 * chiesti, e la proiezione `VoceBot` fa sì che questo componente non li abbia.
 */

import { Link } from 'react-router-dom';
import { Distintivo, Occhiello, Scheda } from '../../componenti';
import { ContenitoreDati, ScheletroLista } from '../../componenti/Stati';
import { IconaStrategia } from '../../componenti/IconaStrategia';
import { etichettaCapitale, etichettaOperativa } from '../../componenti/BotInFunzione';
import { CAMPI_LISTA_BOT, tradebots } from '../../api/risorse';
import { usaRichiesta } from '../../api/usaRichiesta';
import { timeframe } from '../../lib/formato';
import type { Tradebot } from '../../api/tipi';

/** I soli campi di un tradebot che questa schermata ha il diritto di mostrare.
 *  Come `WalletPubblico` per i wallet: una proiezione, non una comodità. */
interface VoceBot {
  id: number;
  strategia: string;
  asset: string;
  valuta: string;
  candleSize: number;
  inFunzione: boolean;
  denaroReale: boolean;
}

function voce(b: Tradebot): VoceBot {
  return {
    id: b.id,
    strategia: b.strategyName,
    asset: b.asset,
    valuta: b.currency,
    candleSize: b.candleSize,
    inFunzione: b.isRunning,
    denaroReale: b.useRealMoney,
  };
}

export function BotDelWallet({ walletId }: { walletId: number }) {
  /**
   * Non esiste un endpoint che dia i bot di un wallet: si scaricano tutti e si
   * filtra qui su `walletId`. È accettabile perché i bot sono una dozzina — la
   * richiesta è annotata in RICHIESTE-API.md (W7) — ma il filtro sta DENTRO la
   * richiesta e non dopo, così `eVuoto` guarda i bot di QUESTO wallet e non
   * l'elenco intero: altrimenti un wallet senza bot, in un sistema che di bot ne
   * ha dodici, comparirebbe come riquadro pieno e vuoto insieme.
   *
   * `totali` viaggia con la risposta perché lo stato vuoto deve poter dire di
   * quanti bot si è guardato: «nessuno dei dodici» è una risposta, «nessuno» da
   * solo è un enigma.
   */
  const richiesta = usaRichiesta(
    async () => {
      const pagina = await tradebots.elenco(CAMPI_LISTA_BOT);
      return {
        totali: pagina.data.length,
        miei: pagina.data.filter((b) => b.walletId === walletId).map(voce),
      };
    },
    [walletId],
    { eVuoto: (d) => d.miei.length === 0 },
  );

  const miei = richiesta.dati?.miei ?? [];
  const totali = richiesta.dati?.totali ?? null;
  const conDenaroVero = miei.filter((b) => b.denaroReale && b.inFunzione).length;

  return (
    <Scheda>
      <div className="sb-w-bot-testata">
        <Occhiello>Tradebot su questo wallet</Occhiello>
        <div className="sb-riga" style={{ gap: 'var(--sb-s-3)' }}>
          {miei.length > 0 && (
            <span className="sb-nota">
              {miei.length} {miei.length === 1 ? 'bot collegato' : 'bot collegati'}
            </span>
          )}
          {/* Il conteggio dei bot che muovono soldi veri sta in testata, prima
              dell'elenco: è il numero che cambia la decisione di svuotare o
              eliminare il wallet, e in fondo alla pagina non lo si rileggerebbe. */}
          {conDenaroVero > 0 && (
            <Distintivo tono="cautela">
              {conDenaroVero} con denaro vero
            </Distintivo>
          )}
        </div>
      </div>

      <ContenitoreDati
        stato={richiesta.stato}
        causaVuoto={
          totali == null
            ? 'Nessun tradebot risulta collegato a questo wallet. Il collegamento è il campo walletId sul bot: qui non punta nessuno.'
            : `Nessuno dei ${totali} tradebot registrati usa questo wallet. Il collegamento è il campo walletId sul bot: su questo indirizzo non opera niente, e il saldo qui sopra non si muove da solo.`
        }
        messaggioErrore={richiesta.errore?.message}
        onRiprova={richiesta.ricarica}
        scheletro={<ScheletroLista righe={2} />}
      >
        <div className="sb-w-bot-elenco">
          {miei.map((b) => (
            <RigaBot key={b.id} bot={b} />
          ))}
        </div>
      </ContenitoreDati>
    </Scheda>
  );
}

/* -------------------------------------------------------------------------- */

function RigaBot({ bot }: { bot: VoceBot }) {
  return (
    <Link to={`/tradebot/${bot.id}`} className="sb-w-bot">
      <IconaStrategia nome={bot.strategia} dim={26} />

      <span className="sb-w-bot__testi">
        <span className="sb-w-bot__nome">
          {bot.strategia}
          <span className="sb-w-bot__id">#{bot.id}</span>
        </span>
        <span className="sb-w-bot__coppia">
          {bot.asset}/{bot.valuta} · {timeframe(bot.candleSize)}
        </span>
      </span>

      <span className="sb-w-bot__stati">
        {/* Ambra per il denaro vero: non dice «bene» né «male» — che sarebbero
            verde e rosso, riservati a guadagno e perdita — dice «qui si rischia». */}
        <Distintivo tono={bot.denaroReale ? 'cautela' : 'neutro'}>
          {etichettaCapitale(bot.denaroReale)}
        </Distintivo>
        <span
          className={`sb-w-bot__operativo${bot.inFunzione ? ' sb-w-bot__operativo--attivo' : ''}`}
        >
          {/* Il marcatore ripete quello che dice il colore: chi non distingue i
              colori legge lo stesso stato. */}
          <span aria-hidden="true">{bot.inFunzione ? '◉ ' : '○ '}</span>
          {etichettaOperativa(bot.inFunzione)}
        </span>
      </span>
    </Link>
  );
}
